import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import styles from '../Dashboard.module.scss';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import { FilterOutlinedIcon, InformationFillIcon } from '~/assets/icons';
import dashboardApi from '~/apis/dashboard';
import { IListAlert } from '~/utils/interface/dashboard';
import { tzDayjs } from '~/utils/helper';
import DetailAlertDialog from '../../alert/component/detail-alert-dialog/DetailAlertDialog';
const cx = classNames.bind(styles);

const WarningSection = () => {
  const { t } = useTranslation('admin/home');

  const [listAlert, setListAlert] = useState<IListAlert[]>([]);

  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [isOpenDetailAlertDialog, setIsOpenDetailAlertDialog] = useState<boolean>(false);
  const [anchorFilter, setAnchorFilter] = useState<null | HTMLElement>(null);
  const isOpenFilter = Boolean(anchorFilter);
  const [selectedFilter, setSelectedFilter] = useState<number>(-1);

  const filterOptions = useMemo(() => {
    return [
      {
        id: -1,
        name: t('warningSection.all'),
        color: '#005F73',
      },
      {
        id: 0,
        name: t('warningSection.0'),
        color: '#ED1002',
      },
      {
        id: 1,
        name: t('warningSection.1'),
        color: '#FF9500',
      },
      {
        id: 2,
        name: t('warningSection.2'),
        color: '#72777B',
      },
    ];
  }, [t]);
  useEffect(() => {
    (async () => {
      try {
        const response = await dashboardApi.getListAlert();
        setListAlert(response.data);
      } catch (error) {}
    })();
  }, []);

  const RenderStatus = (props: { status: number }) => {
    if (props.status === 0) {
      return (
        <Box
          className='xl:w-[117px] xl:h-[23px] lg:w-[90px] lg:h-[18px] rounded-[12px] border border-[#F32A4C] text-[#F32A4C] font-bold text-center xl:leading-[23px] lg:leading-[18px] xl:text-[13px] lg:text-[10px]
      absolute right-0'
        >
          {t('warningSection.' + props.status)}
        </Box>
      );
    } else if (props.status === 2) {
      return (
        <Box
          className='xl:w-[117px] xl:h-[23px] lg:w-[90px] lg:h-[18px] rounded-[12px] border border-[#72777B] text-[#72777B] font-bold text-center xl:leading-[23px] lg:leading-[18px] xl:text-[13px] lg:text-[10px]
      absolute right-0'
        >
          {t('warningSection.' + props.status)}
        </Box>
      );
    } else if (props.status === 1 || props.status === 3) {
      return (
        <Box
          className='xl:w-[117px] xl:h-[23px] lg:w-[90px] lg:h-[18px] rounded-[12px] border border-[#FF9800] text-[#FF9800] font-bold text-center xl:leading-[23px] lg:leading-[18px] xl:text-[13px] lg:text-[10px]
      absolute right-0'
        >
          {t('warningSection.' + props.status)}
        </Box>
      );
    }

    return null;
  };

  const handleDetailClick = (data: { id: string }) => {
    setSelectedAlertId(data.id);
    setIsOpenDetailAlertDialog(true);
  };

  const handleSetOpenDetail = (open: boolean) => {
    setIsOpenDetailAlertDialog(open);
    if (!open) {
      setSelectedAlertId(null);
    }
  };

  return (
    <Box className={cx('rounded-[20px] w-[33%] h-full flex flex-col', 'dashboard-widget')}>
      <Box
        sx={{ backgroundColor: '#ED1002 !important' }}
        className={cx('flex rounded-t-[20px] px-[27px] justify-between items-center', 'dashboard-widget-title')}
      >
        <Typography className={cx('dashboard-widget-title-text')}>{t('fireWarning')}</Typography>
        <IconButton
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setAnchorFilter(event.currentTarget);
          }}
        >
          <FilterOutlinedIcon fill='white' className='lg:h-[20px] xl:h-[24px] lg:w-[19px] xl:w-[23px]' />
        </IconButton>

        <Menu
          open={isOpenFilter}
          anchorEl={anchorFilter}
          onClose={() => setAnchorFilter(null)}
          className='[&>.MuiPaper-root]:!p-0 [&>.MuiPaper-root]:min-w-[130px]'
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {filterOptions.map((option, index) => {
            return (
              <MenuItem
                onClick={() => {
                  setSelectedFilter(option.id);
                  setAnchorFilter(null);
                }}
                className={`${
                  index !== filterOptions.length - 1
                    ? '!border-b border-solid border-b-[#DFDFDF] !rounded-none p-[30px]'
                    : ''
                } ${option.id === selectedFilter ? 'bg-[#E3F1FF]' : ''}`}
              >
                <Box className={`text-[${option.color}] w-full font-bold text-center`}>{option.name}</Box>
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
      <Box className={cx('border border-[#DFDFDF] rounded-b-[20px] xl:p-5 lg:p-2', 'dashboard-widget-body')}>
        <Box className='border border-[#DFDFDF] overflow-auto h-full'>
          {listAlert
            ?.filter((item) => {
              if (selectedFilter === -1) return true;

              return selectedFilter === item.status;
            })
            ?.map((item) => {
              return (
                <Box
                  className='flex justify-between border border-b-[#005F73] xl:px-5 xl:py-3 lg:px-3 lg:py-2'
                  key={item.id}
                >
                  <Box>
                    <Typography className='xl:text-[13px] lg:text-[10px]'>
                      {item.created_at ? tzDayjs(item.created_at).format('DD/MM/YYYY HH:mm') : ''}
                    </Typography>
                    <Typography className='xl:text-[13px] lg:text-[10px]'>{item.location}</Typography>
                  </Box>
                  <Box className='flex items-center gap-3'>
                    <Box className='relative h-full'>
                      <Typography className='font-bold xl:text-[13px] lg:text-[10px]'>{item.sensor_code}</Typography>
                      <RenderStatus status={item.status} />
                    </Box>
                    <InformationFillIcon
                      fill='#D9D9D9'
                      className='lg:h-[15px] xl:h-[25px] lg:w-[15px] xl:w-[25px] hover:cursor-pointer'
                      onClick={() => handleDetailClick({ id: item.id })}
                    />
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>
      <DetailAlertDialog open={isOpenDetailAlertDialog} data={{ id: selectedAlertId }} setOpen={handleSetOpenDetail} />
    </Box>
  );
};

export default memo(WarningSection);
